import {
  Box,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";
import { BarragemData, useBarragemContext } from "../../context";
import { useEffect, useRef, useState } from "react";
import { RequestError } from "../../services";
import { Cadastro, PolyZas, PolyZss } from "../zas";
import { barragemLayers } from "./layersBarragemMap";
import { googleSatelite, HidroLayer, HidroLayerGroup, HidroLayersComponent, HidroMap } from "../mapa";
import { ErrorOnDialog } from "../modals";
import Logo from "../../assets/logoSemFundo.png";

interface Props {
  sx?: SxProps<Theme>;
}
export const EmpreendimentoMap: React.FC<Props> = ({ sx }) => {
  const theme = useTheme();
  const { barragem } = useBarragemContext();

  const layers = useRef(new HidroLayerGroup({
    name: "Layers",
    showing: true,
    layers: [],
  }));
  const timer = useRef<NodeJS.Timeout>();

  const [error, setError] = useState<RequestError>();

  useEffect(() => {
    if (!barragem) return;
    if ("error" in barragem) {
      setError(barragem);
      return;
    }

    timer.current = setTimeout(() => {
      getLayers(barragem, theme, layers.current);
      layers.current.centerBounds({ animate: false });
    }, 500);

    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [barragem]);

  return <>
    <ErrorOnDialog
      open={Boolean(error)}
      error={error?.error ?? ""}
      message={error?.message ?? ""}
      onClose={() => setError(undefined)}
    />

    <Box sx={{
      ...sx,
      flex: 1,
      width: "100%",
      height: "100%",
      display: "flex",
      flexDirection: "row",
    }}>

      {/* Mapa */}
      <div style={{ flex: 80 }}>
        <HidroMap
          recenter
          chooseMap
          expandable
          screenshot
          startWithTile
          animateStart={false}
          hidroLayers={layers.current}
        />
      </div>

      {/* Layers */}
      <Box sx={{
        flex: 20,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingX: "0.5em",
        paddingY: "0.3em",
      }}>
        {layers.current && <HidroLayersComponent hidroLayers={layers.current} />}
        <img
          src={Logo}
          alt={"logo"}
          style={{
            objectFit: "contain",
            height: `7em`,
            width: `7em`,
          }}
        />
      </Box>

    </Box>
  </>;
};


const getLayers = (barragemData: BarragemData, theme: Theme, layers: HidroLayerGroup) => {
  layers.clearLayers();
  layers.push(barragemLayers(barragemData, theme, false));

  // Dados da ZAS
  if (barragemData.zas && !("error" in barragemData.zas)) {

    // ZAS
    if (barragemData?.zas?.polygon && barragemData.zas.polygon instanceof PolyZas) {
      layers.push(new HidroLayer({
        name: "ZAS",
        showing: true,
        layer: barragemData.zas.polygon,
      }));
    }

    // Mancha de ZSS
    if (barragemData?.zas?.polygonZss && barragemData.zas.polygonZss instanceof PolyZss) {
      layers.push(new HidroLayer({
        name: "ZSS",
        showing: true,
        layer: barragemData.zas.polygonZss,
      }));
    }

    // Rotas de Fuga
    if (barragemData.zas.rotasFuga && !('error' in barragemData.zas.rotasFuga)) {
      const layerRotas = new HidroLayerGroup({ name: "Rotas de Fuga", showing: true, layers: [] });

      barragemData.zas.rotasFuga.forEach(rota => {
        layerRotas.push(new HidroLayer({
          name: `R${rota.properties.nome}`,
          showing: true,
          layer: rota,
          style: theme.palette.geometries.rotaFuga,
        }));
      });
      layers.push(layerRotas);
    }

    // Potos de encontro
    if (barragemData?.zas?.pontosEncontro && !('error' in barragemData.zas.pontosEncontro)) {
      layers.push(new HidroLayerGroup({
        name: "Pontos de Encontro",
        showing: true,
        layers: barragemData.zas.pontosEncontro.map(layer => new HidroLayer({
          name: layer.properties.ponto,
          showing: true,
          layer: layer,
        })),
      }));
    }

    // Unidades Sensíveis
    if (barragemData?.zas?.uniSensivel && !('error' in barragemData.zas.uniSensivel)) {
      layers.push(new HidroLayerGroup({
        name: "Uni. Sensíveis",
        showing: true,
        layers: barragemData.zas.uniSensivel.map(layer => new HidroLayer({
          name: layer.properties.nome,
          showing: true,
          layer: layer,
          style: layer.properties.style,
        })),
      }));
    }

    // Cadastros
    if (barragemData?.zas?.cadastros && !('error' in barragemData.zas.cadastros)) {
      const cadastrosPorTipo: { [key: string]: Cadastro[]; } = {};
      for (const cadastro of barragemData.zas.cadastros) {
        const tipo = cadastro.properties.tipo || 'Outros';
        if (!cadastrosPorTipo[tipo]) {
          cadastrosPorTipo[tipo] = [];
        }
        cadastrosPorTipo[tipo].push(cadastro);
      }

      const subGrupos = Object.entries(cadastrosPorTipo).map(([tipo, cadastrosDoTipo]) => {
        return new HidroLayerGroup({
          name: tipo,
          showing: true,
          layers: cadastrosDoTipo.map((cadastro) => new HidroLayer({
            name: `Cadastro ${cadastro.properties.id}`,
            showing: true,
            layer: cadastro,
          })),
        });
      });

      layers.push(new HidroLayerGroup({
        name: "Cadastro ZAS",
        showing: true,
        layers: subGrupos,
      }));
    }

    // Sirenes
    if (barragemData?.zas?.sirenes && !('error' in barragemData.zas.sirenes)) {
      layers.push(new HidroLayerGroup({
        name: "Sirenes",
        showing: true,
        layers: barragemData.zas.sirenes.map((layer, i) => {
          const layersAdd = [
            new HidroLayer({
              name: "Sirene",
              showing: true,
              layer: layer,
            })
          ];
          if (layer.properties.areaCobertura) {
            layersAdd.push(new HidroLayer({
              name: "Cobertura",
              showing: true,
              layer: layer.properties.areaCobertura,
            }));
          }

          return new HidroLayerGroup({
            name: `Sirene ${i + 1}`,
            showing: true,
            layers: layersAdd,
          });
        }),
      }));
    }

    // Acomodações
    if (barragemData?.zas?.acomodacoes && !('error' in barragemData.zas.acomodacoes)) {
      layers.push(new HidroLayerGroup({
        name: "Acomodações",
        showing: true,
        layers: barragemData.zas.acomodacoes.map(layer => new HidroLayer({
          name: layer.properties.nome,
          showing: true,
          layer: layer,
          style: layer.style || { iconStr: "Hotel", color: "#583506", iconSize: 25 },
        })),
      }));
    }

    // Rotas Alternativas
    if (barragemData?.zas?.rotasAlternativas && !('error' in barragemData.zas.rotasAlternativas)) {
      layers.push(new HidroLayerGroup({
        name: "Rotas Alternativas",
        showing: true,
        layers: barragemData.zas.rotasAlternativas.map(layer => new HidroLayer({
          name: layer.properties.nome,
          showing: true,
          layer: layer,
        })),
      }));
    }
  }

  layers.push(new HidroLayer({
    name: "Google Satelite",
    showing: true,
    layer: googleSatelite(),
  }));

  return layers;
};
